import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/shieldx/Logo";
import { apiGet, apiPut, timeAgo } from "@/lib/shieldx";
import {
  LayoutDashboard, ShieldCheck, Building2, Boxes, KeyRound, Plug, ShieldAlert, Siren,
  ScrollText, Activity, FileClock, FileBarChart, OctagonAlert, Settings,
  Search, Bell, LogOut, Menu, X, ChevronRight, Radar, Crosshair, ShieldHalf,
  BadgeCheck, Fingerprint, Gauge, KeySquare, Webhook, ScanSearch, FileLock2, UsersRound,
  Network, Timer, MemoryStick, Router, ShieldUser, Satellite, Bot, Globe, LogIn, Atom, Rocket, Workflow, HeartPulse,
  Crown, Landmark, Mail, SlidersHorizontal, ClipboardList,
} from "lucide-react";

const SUPER_ADMIN_NAV = [
  { to: "/app/super-admin", label: "SA Command Center", icon: Crown, end: true },
  { to: "/app/super-admin/organizations", label: "Organizations", icon: Building2 },
  { to: "/app/super-admin/creators", label: "Creators", icon: ShieldUser },
  { to: "/app/super-admin/government", label: "Government", icon: Landmark },
  { to: "/app/super-admin/defense", label: "Defense", icon: ShieldHalf },
  { to: "/app/super-admin/aerospace", label: "Aerospace", icon: Satellite },
  { to: "/app/super-admin/users", label: "Users", icon: UsersRound },
  { to: "/app/super-admin/registrations", label: "Registrations", icon: ClipboardList },
  { to: "/app/super-admin/assets", label: "Assets", icon: Boxes },
  { to: "/app/super-admin/incidents", label: "Incidents", icon: Siren },
  { to: "/app/super-admin/policies", label: "Policies", icon: ScrollText },
  { to: "/app/super-admin/screening", label: "Security Screening", icon: ScanSearch },
  { to: "/app/super-admin/email", label: "Email & Comms", icon: Mail },
  { to: "/app/super-admin/audit", label: "Super Admin Audit", icon: FileLock2 },
  { to: "/app/super-admin/settings", label: "Platform Settings", icon: SlidersHorizontal },
];

const NAV = [
  { to: "/app/command-center", label: "Command Center", icon: LayoutDashboard },
  { to: "/app/notifications", label: "Notification Center", icon: Bell },
  { to: "/app/security-pipeline", label: "Security Pipeline", icon: Workflow },
  { to: "/app/verification", label: "Verification", icon: BadgeCheck },
  { to: "/app/protection", label: "Protection", icon: ShieldCheck },
  { to: "/app/organizations", label: "Organizations", icon: Building2 },
  { to: "/app/assets", label: "Assets", icon: Boxes },
  { to: "/app/authorization", label: "Authorization", icon: KeyRound },
  { to: "/app/zk-authorization", label: "Zero-Knowledge Auth", icon: KeySquare },
  { to: "/app/ai-access", label: "Access Control", icon: Radar },
  { to: "/app/defense-wall", label: "Defense Wall", icon: ShieldHalf },
  { to: "/app/browser-integrity", label: "Browser Integrity", icon: Fingerprint },
  { to: "/app/throttling", label: "Economic Throttling", icon: Gauge },
  { to: "/app/shadow-ai", label: "Shadow AI Discovery", icon: ScanSearch },
  { to: "/app/threat-trap", label: "AI Threat Trap", icon: Webhook },
  { to: "/app/threats", label: "Threats", icon: ShieldAlert },
  { to: "/app/incidents", label: "Incidents", icon: Siren },
  { to: "/app/policies", label: "Policies", icon: ScrollText },
  { to: "/app/soc", label: "Security Operations", icon: Activity },
  { to: "/app/simulation", label: "Attack Simulation", icon: Crosshair },
  { to: "/app/consensus", label: "Consensus Firewall", icon: Network },
  { to: "/app/hardware-authz", label: "Hardware Authorization", icon: Fingerprint },
  { to: "/app/login-guard", label: "Ultimate Login Guard", icon: LogIn },
  { to: "/app/side-channel", label: "Side-Channel Defense", icon: Timer },
  { to: "/app/memory-state", label: "Memory & Runtime", icon: MemoryStick },
  { to: "/app/network-quarantine", label: "Network Quarantine", icon: Router },
  { to: "/app/adversarial", label: "Adversarial AI Defense", icon: Bot },
  { to: "/app/website-resilience", label: "Website Resilience", icon: Globe },
  { to: "/app/creator-guard", label: "Creator Guard", icon: ShieldUser },
  { to: "/app/defense", label: "ShieldX Defense", icon: ShieldHalf },
  { to: "/app/aerospace", label: "Aerospace Edition", icon: Satellite },
  { to: "/app/command-validation", label: "Command Validation", icon: Rocket },
  { to: "/app/quantum-defense", label: "Quantum Defense", icon: Atom },
  { to: "/app/self-healing", label: "Self-Healing Infra", icon: HeartPulse },
  { to: "/app/access-governance", label: "Access Governance", icon: UsersRound },
  { to: "/app/integrations", label: "Integrations", icon: Plug },
  { to: "/app/audit-vault", label: "Audit Vault", icon: FileLock2 },
  { to: "/app/audit-logs", label: "Audit Logs", icon: FileClock },
  { to: "/app/reports", label: "Reports", icon: FileBarChart },
  { to: "/app/platform-security", label: "Platform Security", icon: ShieldCheck },
  { to: "/app/emergency", label: "Emergency Control", icon: OctagonAlert },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function SidebarInner({ onNavigate }) {
  const { user } = useAuth();
  const isSA = user?.role === "super_admin";
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-800 px-5">
        <NavLink to="/app/command-center" onClick={onNavigate} data-testid="sidebar-logo">
          <Wordmark />
        </NavLink>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {isSA && (
          <div className="mb-3">
            <p className="px-3 pb-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-400/70">Super Admin Governance</p>
            {SUPER_ADMIN_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
                data-testid={`sa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn("group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive ? "bg-amber-500/10 text-amber-200 ring-1 ring-inset ring-amber-500/30"
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100")}>
                <n.icon className="h-4.5 w-4.5 shrink-0" size={18} />
                <span className="truncate">{n.label}</span>
              </NavLink>
            ))}
            <div className="mx-3 my-3 border-t border-slate-800" />
            <p className="px-3 pb-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-600">Platform Modules</p>
          </div>
        )}
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn("group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/30"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100")
            }
          >
            <n.icon className="h-4.5 w-4.5 shrink-0" size={18} />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600">Zero-Trust · Default Deny</p>
        <p className="mt-1 text-[11px] text-slate-500">Capability ≠ Authority</p>
        <p className="mt-2 font-mono text-[10px] leading-tight text-slate-600">Powered by Vijay Software Solutions Pvt Ltd</p>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 1) { setResults([]); return; }
      try { const d = await apiGet(`/search?q=${encodeURIComponent(q)}`); setResults(d.results || []); setOpen(true); }
      catch { setResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (r) => {
    setOpen(false); setQ("");
    const map = { organizations: "organizations", assets: "assets", ai_agents: "ai-agents", integrations: "integrations", threats: "threats", incidents: "incidents" };
    if (r.collection === "incidents") navigate(`/app/incidents/${r.id}`);
    else navigate(`/app/${map[r.collection] || "command-center"}`);
  };

  return (
    <div className="relative w-full max-w-md" ref={box}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Search org, domain, asset, incident, IP, agent…"
        className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 max-h-96 w-full overflow-y-auto rounded-lg border border-slate-800 bg-slate-900 shadow-2xl" data-testid="search-results">
          {results.map((r, i) => (
            <button key={i} onClick={() => go(r)}
              className="flex w-full items-center justify-between gap-3 border-b border-slate-800/70 px-4 py-2.5 text-left hover:bg-slate-800/60">
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-200">{r.label}</p>
                <p className="truncate font-mono text-[11px] text-slate-500">{r.sub}</p>
              </div>
              <span className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">{r.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const navigate = useNavigate();
  const load = async () => { try { const d = await apiGet("/notifications"); setItems(Array.isArray(d) ? d : []); } catch {} };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const unread = items.filter((i) => !i.read).length;
  const tone = { EMERGENCY: "text-rose-400", CRITICAL: "text-red-400", HIGH: "text-orange-400", MEDIUM: "text-amber-300", LOW: "text-emerald-400", NOTICE: "text-cyan-300", INFO: "text-sky-400" };
  const openItem = async (n) => { setOpen(false); try { await apiPut(`/notifications/${n.id}/read`, {}); } catch {} navigate(n.deep_link || "/app/notifications"); };
  return (
    <div className="relative" ref={box}>
      <button data-testid="notifications-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-slate-800 bg-slate-900/70 text-slate-300 hover:text-white">
        <Bell className="h-4.5 w-4.5" size={18} />
        {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-slate-800 bg-slate-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
            <span className="font-display text-sm font-semibold text-slate-100">Notifications</span>
            <button data-testid="notif-view-all" onClick={() => { setOpen(false); navigate("/app/notifications"); }} className="text-[11px] font-medium text-blue-400 hover:text-blue-300">View all</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-500">NO NEW NOTIFICATIONS</p>}
            {items.slice(0, 12).map((n) => (
              <button key={n.id} onClick={() => openItem(n)}
                className={cn("block w-full border-b border-slate-800/70 px-4 py-3 text-left hover:bg-slate-800/50", !n.read && "bg-slate-800/30")}>
                <div className="flex items-center justify-between">
                  <span className={cn("font-mono text-[10px] font-bold uppercase tracking-widest", tone[n.severity])}>{n.severity} · {n.category}</span>
                  <span className="text-[10px] text-slate-500">{timeAgo(n.created_at)}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-slate-200">{n.title}</p>
                <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{n.summary || n.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SecureAccess() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  if (user?.role !== "super_admin") return null;
  return (
    <div className="relative hidden sm:block" ref={box}>
      <button data-testid="secure-access-indicator" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-500/20">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> PRIVILEGED SESSION
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-72 rounded-lg border border-slate-800 bg-slate-900 p-4 shadow-2xl">
          <p className="font-display text-sm font-semibold text-slate-100">ShieldX Secure Access</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-emerald-400"><BadgeCheck className="h-3.5 w-3.5" /> Identity verified · server-side authorized</p>
          <div className="mt-3 space-y-1.5 text-xs">
            {[["Role", user?.role], ["Email", user?.email], ["Organization", user?.org_id], ["Auth Method", "Password + step-up re-auth"], ["Step-Up", "Required for high-impact actions"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className="truncate font-mono text-slate-300">{String(v || "—")}</span></div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-slate-600">Not MFA — real MFA/passkey not wired. Dangerous actions require password re-authentication.</p>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-800 bg-slate-900/60 lg:block">
        <SidebarInner />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 border-r border-slate-800 bg-slate-900">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 text-slate-400" data-testid="close-mobile-nav"><X className="h-5 w-5" /></button>
            <SidebarInner onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-800 bg-slate-950/85 px-4 backdrop-blur-xl lg:px-8">
          <button onClick={() => setMobileOpen(true)} className="text-slate-300 lg:hidden" data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2.5">
            <SecureAccess />
            <button onClick={() => navigate("/app/emergency")} data-testid="emergency-quick-link"
              className="hidden items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/20 sm:flex">
              <OctagonAlert className="h-4 w-4" /> Emergency
            </button>
            <NotificationsBell />
            <div className="group relative">
              <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/70 py-1.5 pl-1.5 pr-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600/20 font-display text-xs font-bold text-blue-300">
                  {(user?.name || "U").slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-xs font-semibold leading-tight text-slate-200">{user?.name}</span>
                  <span className="block font-mono text-[10px] leading-tight text-slate-500">{user?.role}</span>
                </span>
              </button>
              <div className="invisible absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-slate-800 bg-slate-900 p-1 opacity-0 shadow-2xl transition-all group-hover:visible group-hover:opacity-100">
                <div className="truncate px-3 py-2 text-xs text-slate-500">{user?.email}</div>
                <button onClick={logout} data-testid="logout-button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="min-h-[calc(100vh-4rem)] p-4 lg:p-8">
          <div className="sx-fadein mx-auto max-w-[1500px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export { ChevronRight };
